import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { signInWithEmail, signInWithStrava, signUpWithEmail } from '../../auth/actions';
import { ConsentCheckbox } from '../../components/ConsentCheckbox';
import { StravaConnectButton } from '../../components/StravaConnectButton';
import { CONSENT_REQUIRED, notePendingConsent, takePendingConsent } from '../../lib/consent';
import styles from './SignInPage.module.css';

export type AuthMode = 'sign-in' | 'sign-up';

const COPY = {
  'sign-in': { eyebrow: 'WELCOME BACK', title: 'SIGN IN', submit: 'SIGN IN →', other: 'NEW HERE? CREATE ACCOUNT ↗' },
  'sign-up': { eyebrow: 'NEW HERE', title: 'CREATE ACCOUNT', submit: 'CREATE ACCOUNT →', other: 'HAVE AN ACCOUNT? SIGN IN ↗' },
} as const;

interface Values { name: string; email: string; password: string }

interface TextFieldProps {
  label: string;
  type: 'text' | 'email' | 'password';
  value: string;
  autoComplete: string;
  onChange: (value: string) => void;
}

function TextField({ label, type, value, autoComplete, onChange }: TextFieldProps) {
  return (
    <label className={styles.field}>
      <span className={styles.label}>{label}</span>
      <input
        className={styles.input} type={type} value={value} autoComplete={autoComplete} required
        minLength={type === 'password' ? 8 : undefined} onChange={(e) => onChange(e.currentTarget.value)}
      />
    </label>
  );
}

function Fields({ mode, values, onChange }: { mode: AuthMode; values: Values; onChange: (next: Values) => void }) {
  return (
    <>
      {mode === 'sign-up' && (
        <TextField label="NAME" type="text" autoComplete="name" value={values.name} onChange={(name) => onChange({ ...values, name })} />
      )}
      <TextField label="EMAIL" type="email" autoComplete="email" value={values.email} onChange={(email) => onChange({ ...values, email })} />
      <TextField
        label="PASSWORD" type="password" value={values.password}
        autoComplete={mode === 'sign-up' ? 'new-password' : 'current-password'}
        onChange={(password) => onChange({ ...values, password })}
      />
    </>
  );
}

type Via = 'email' | 'strava';

// Strava sign-in can create an account, so it needs the agreement as email sign-up does; email sign-in is an account
// that exists, and the app asks it once if it never agreed.
const needsConsent = (mode: AuthMode, via: Via): boolean => via === 'strava' || mode === 'sign-up';

async function authenticate(mode: AuthMode, via: Via, values: Values): Promise<void> {
  // Strava leaves the page before there is a session, so the agreement goes with it. Email drops any note an earlier
  // Strava start left (say, the page came back from the browser's back button), and a sign-up notes it only once its
  // account exists. A failed attempt drops the note.
  if (via === 'strava') notePendingConsent();
  else takePendingConsent();
  const result = await (via === 'strava' ? signInWithStrava()
    : mode === 'sign-up' ? signUpWithEmail(values) : signInWithEmail(values.email, values.password));
  if (!result.ok) {
    takePendingConsent();
    throw new Error(result.message);
  }
  if (via === 'email' && mode === 'sign-up') notePendingConsent();
}

export function AuthForm({ mode, initialError }: { mode: AuthMode; initialError: string | null }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Values>({ name: '', email: '', password: '' });
  const [agreed, setAgreed] = useState(false);
  // Signing in by email needs no agreement, so the box shows there only once Strava is chosen.
  const [stravaChosen, setStravaChosen] = useState(false);
  const auth = useMutation({
    mutationFn: async (via: Via): Promise<Via> => {
      if (needsConsent(mode, via) && !agreed) throw new Error(CONSENT_REQUIRED);
      await authenticate(mode, via, values);
      return via;
    },
    onSuccess: async (via) => {
      if (via === 'strava') return;
      queryClient.clear();
      await navigate({ to: mode === 'sign-up' ? '/onboarding/ftp' : '/' });
    },
  });
  const startStrava = (): void => {
    setStravaChosen(true);
    auth.mutate('strava');
  };
  // A Strava start that succeeded is redirecting away, so the buttons stay busy.
  const busy = auth.isPending || (auth.isSuccess && auth.data === 'strava');
  const error = auth.isIdle ? initialError : (auth.error?.message ?? null);
  const consentError = error === CONSENT_REQUIRED ? error : null;
  const copy = COPY[mode];

  return (
    <form className={styles.form} onSubmit={(event) => { event.preventDefault(); auth.mutate('email'); }} aria-labelledby="auth-title">
      <div className={styles.head}>
        <p className={styles.eyebrow}>{copy.eyebrow}</p>
        <h2 id="auth-title" className={styles.title}>{copy.title}</h2>
      </div>
      {(mode === 'sign-up' || stravaChosen) && <ConsentCheckbox checked={agreed} onChange={setAgreed} error={consentError} />}
      <div className={styles.strava}><StravaConnectButton onClick={startStrava} busy={busy} /></div>
      <div className={styles.divider}><span>OR EMAIL</span></div>
      <Fields mode={mode} values={values} onChange={setValues} />
      {error !== null && consentError === null && <p role="alert" className="alert-hot">{error}</p>}
      <button type="submit" className="btn btn-ink btn-block" disabled={busy}>{copy.submit}</button>
      <Link to="/sign-in" search={mode === 'sign-up' ? {} : { mode: 'sign-up' }} className={styles.switch}>{copy.other}</Link>
    </form>
  );
}
